import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useInsights } from '../hooks/useInsights';

const SERVICE_INFO = {
  'LinkedIn_Automation': { icon: '🔗', short: 'LinkedIn', desc: 'LinkedIn outreach & lead generation' },
  'Email_Marketing':     { icon: '📧', short: 'Email', desc: 'Email campaigns & automation' },
  'Content_Creation':    { icon: '✍️', short: 'Content', desc: 'Blog, newsletter & SEO content' },
  'SEO':                 { icon: '🔍', short: 'SEO', desc: 'Search engine optimization' },
  'BD_for_Clients':      { icon: '🤝', short: 'Business Dev', desc: 'Finding & introducing partners' },
  'Retention_Marketing': { icon: '🔄', short: 'Retention', desc: 'Customer retention & CRM' },
  'Podcast_&_Video_Production': { icon: '🎬', short: 'Podcast & Video', desc: 'Podcast & video production' },
  'Website_UI/UX_Design':      { icon: '🎨', short: 'Website Design', desc: 'Website design & UX' },
  'Website_Development':        { icon: '💻', short: 'Website Dev', desc: 'Website development' },
  'Performance_Marketing':      { icon: '📈', short: 'Paid Ads', desc: 'Paid advertising campaigns' },
  'Media_&_PR':                 { icon: '📰', short: 'Media & PR', desc: 'Press coverage & media relations' },
  'Logo_&_Brand_Guidelines':   { icon: '🏷️', short: 'Branding', desc: 'Brand identity & guidelines' },
  'Creative_&_Video':           { icon: '🎥', short: 'Creative & Video', desc: 'Creative assets & video' },
  'Outsourced_Head_of_Operations': { icon: '📋', short: 'Operations', desc: 'Operations management' }
};

const STATUS = {
  'On Track':  { icon: '✅', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  'At Risk':   { icon: '⚠️', color: '#ea580c', bg: '#fff7ed', border: '#fed7aa' },
  'Delayed':   { icon: '🔴', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
  'Unknown':   { icon: '⚪', color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb' }
};

const FALSE_BLOCKERS = new Set(['on schedule — no issues','on schedule - no issues','on schedule','no issues','none','n/a','na','-','','no blockers','no blockers at the moment','no current blockers']);

function norm(s) {
  if (!s) return 'Unknown';
  const l = s.toLowerCase();
  if (l.includes('track')) return 'On Track';
  if (l.includes('risk') || l.includes('off track')) return 'At Risk';
  if (l.includes('delay') || l.includes('blocked')) return 'Delayed';
  return 'Unknown';
}

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function ProjectHistory() {
  const { projectName } = useParams();
  const { data, loading, error } = useInsights();
  const [filterForm, setFilterForm] = useState('all');

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <EmptyState />;
  const project = data.projects?.find(p => p.project_name === decodeURIComponent(projectName));
  if (!project) return <NotFound name={projectName} />;

  const cfg = STATUS[project.overall_status] || STATUS['Unknown'];
  const forms = [...new Set((project.history || []).map(h => h.form))];
  const allTimeline = (project.history || [])
    .filter(h => h.date && !isNaN(new Date(h.date).getTime()))
    .sort((a, b) => new Date(b.date) - new Date(a.date));
  const timeline = filterForm === 'all' ? allTimeline : allTimeline.filter(h => h.form === filterForm);

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      <header style={{ background: 'white', borderBottom: '1px solid #e5e7eb', padding: '12px 24px', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <Link to="/" style={{ fontSize: 13, color: '#6366f1', textDecoration: 'none', fontWeight: 500 }}>← All Projects</Link>
            <Link to={`/project/${encodeURIComponent(project.project_name)}`} style={{ fontSize: 13, color: '#6366f1', textDecoration: 'none', fontWeight: 500 }}>← {project.project_name}</Link>
          </div>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 16, fontSize: 12, fontWeight: 600, background: cfg.bg, color: cfg.color }}>
            {cfg.icon} {project.overall_status === 'At Risk' ? 'Needs Attention' : project.overall_status}
          </span>
        </div>
      </header>

      <main style={{ maxWidth: 900, margin: '0 auto', padding: '24px' }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#111', marginBottom: 4 }}>{project.project_name} · Update History</h1>
        <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>
          {allTimeline.length} updates across {forms.length} service{forms.length !== 1 ? 's' : ''} · Managed by {project.account_manager}
        </p>

        {forms.length > 1 && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
            <button onClick={() => setFilterForm('all')} style={{
              padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: filterForm === 'all' ? 600 : 400,
              border: filterForm === 'all' ? '2px solid #6366f1' : '1px solid #d1d5db',
              background: filterForm === 'all' ? '#eff6ff' : 'white', color: filterForm === 'all' ? '#4f46e5' : '#6b7280', cursor: 'pointer'
            }}>All services</button>
            {forms.map(f => {
              const meta = SERVICE_INFO[f] || { icon: '📌', short: f };
              const active = filterForm === f;
              return <button key={f} onClick={() => setFilterForm(f)} style={{
                padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: active ? 600 : 400,
                border: active ? '2px solid #6366f1' : '1px solid #d1d5db',
                background: active ? '#eff6ff' : 'white', color: active ? '#4f46e5' : '#6b7280', cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 4
              }}>{meta.icon} {meta.short}</button>;
            })}
          </div>
        )}

        {timeline.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
            <p style={{ fontSize: 16 }}>No updates recorded yet</p>
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', left: 19, top: 12, bottom: 12, width: 2, background: '#e5e7eb' }} />
            {timeline.map((entry, i) => {
              const ecfg = STATUS[norm(entry.status)] || STATUS['Unknown'];
              const rc = entry.richContent || {};
              const smeta = SERVICE_INFO[entry.form] || { icon: '📌', short: entry.form };
              const week = entry.week || fmtDate(entry.date);

              // Build content
              let content = '';
              if (entry.summary && entry.summary !== 'No updates' && entry.summary !== 'No updates provided' && entry.summary !== 'No updates available') {
                content = entry.summary;
              } else {
                const parts = [];
                if (rc.notes && rc.notes !== 'N/A' && rc.notes !== 'No updates') parts.push(rc.notes);
                if (rc.activity && rc.activity !== 'N/A' && rc.activity !== 'No activities happened') parts.push(rc.activity);
                if (rc.completedThisWeek && rc.completedThisWeek !== 'N/A') parts.push(rc.completedThisWeek);
                if (rc.contentDelivered && rc.contentDelivered !== 'N/A') parts.push(rc.contentDelivered);
                if (rc.deliveryThisWeek && rc.deliveryThisWeek !== 'N/A') parts.push(rc.deliveryThisWeek);
                if (rc.campaignsExecuted && rc.campaignsExecuted !== 'N/A') parts.push(rc.campaignsExecuted.slice(0, 100));
                if (rc.emailsSent && rc.emailsSent !== 'N/A') parts.push(`Emails: ${rc.emailsSent}`);
                if (rc.connectionRequests) parts.push(`${rc.connectionRequests} connections`);
                if (rc.meetingsHeld && rc.meetingsHeld !== 'N/A') parts.push(`Meetings: ${rc.meetingsHeld}`);
                content = parts.join(' · ');
              }

              // Metrics
              const metrics = [];
              if (rc.emailsSent && rc.emailsSent !== 'N/A') metrics.push({ l: 'Emails sent', v: rc.emailsSent });
              if (rc.connectionRequests) metrics.push({ l: 'Connections', v: rc.connectionRequests });
              if (rc.positiveReplies) metrics.push({ l: 'Replies', v: rc.positiveReplies });
              if (rc.meetingsHeld && rc.meetingsHeld !== 'N/A') metrics.push({ l: 'Meetings', v: rc.meetingsHeld });
              if (rc.conversions && rc.conversions !== 'N/A') metrics.push({ l: 'Conversions', v: rc.conversions });
              if (rc.openRate) metrics.push({ l: 'Open rate', v: rc.openRate });
              if (rc.clickRate) metrics.push({ l: 'Click rate', v: rc.clickRate });
              if (rc.churnRiskAccounts) metrics.push({ l: 'Accounts at risk', v: rc.churnRiskAccounts });
              if (rc.publishedVsTarget && rc.publishedVsTarget !== 'N/A') metrics.push({ l: 'Delivered vs target', v: rc.publishedVsTarget });
              if (rc.episodesDelivered) metrics.push({ l: 'Episodes', v: rc.episodesDelivered });
              if (rc.satisfactionSignals && rc.satisfactionSignals !== 'N/A') metrics.push({ l: 'Client sentiment', v: rc.satisfactionSignals.slice(0, 50) });
              if (rc.keywordMovements && rc.keywordMovements !== 'N/A') metrics.push({ l: 'Keyword movements', v: rc.keywordMovements });
              if (rc.contentStatus && rc.contentStatus !== 'N/A') metrics.push({ l: 'Content status', v: rc.contentStatus });
              if (rc.activeVsTotal && rc.activeVsTotal !== 'N/A') metrics.push({ l: 'Campaigns active', v: rc.activeVsTotal });

              // Separate: blocked, reason, pending
              const isBlocked = entry.blockers && entry.blockers.trim() && !FALSE_BLOCKERS.has(entry.blockers.trim().toLowerCase());
              const blockedText = isBlocked ? entry.blockers.trim() : null;

              const hasReason = rc.flags && !FALSE_BLOCKERS.has(rc.flags.trim().toLowerCase());
              const reasonFlag = hasReason ? rc.flags.trim() : null;
              const reasonExplanation = (hasReason && rc.notes && rc.notes !== 'N/A' && rc.notes !== 'No updates') ? rc.notes.trim() : null;

              const nextPlan = (rc.planNextWeek && rc.planNextWeek !== 'N/A' && rc.planNextWeek !== 'No Plans') ? rc.planNextWeek : null;

              // What's working well
              const winsEntry = [];
              if (rc.completedThisWeek && rc.completedThisWeek !== 'N/A' && rc.completedThisWeek.trim()) winsEntry.push(rc.completedThisWeek.trim().substring(0, 120));
              else if (rc.contentDelivered && rc.contentDelivered !== 'N/A' && rc.contentDelivered.trim()) winsEntry.push(rc.contentDelivered.trim().substring(0, 120));
              else if (rc.deliveryThisWeek && rc.deliveryThisWeek !== 'N/A' && rc.deliveryThisWeek.trim()) winsEntry.push(rc.deliveryThisWeek.trim().substring(0, 120));
              if (rc.conversions && rc.conversions !== 'N/A' && rc.conversions.trim()) winsEntry.push('Conversions: ' + rc.conversions.trim());
              if (rc.meetingsHeld && rc.meetingsHeld !== 'N/A' && rc.meetingsHeld.trim()) winsEntry.push('Meetings held: ' + rc.meetingsHeld.trim());
              if (rc.renewalsUpsells && rc.renewalsUpsells !== 'N/A' && rc.renewalsUpsells.trim()) winsEntry.push(rc.renewalsUpsells.trim().substring(0, 120));
              if (rc.satisfactionSignals && rc.satisfactionSignals !== 'N/A' && !rc.satisfactionSignals.toLowerCase().startsWith('no negative')) winsEntry.push(rc.satisfactionSignals.trim().substring(0, 120));

              // Needs follow-up
              const followupItems = [];
              const notesLower = (rc.notes || '').toLowerCase();
              if (notesLower.includes('follow up') || notesLower.includes('follow-up') || notesLower.includes('followup') || notesLower.includes('awaiting') || notesLower.includes('waiting for') || notesLower.includes('pending from')) followupItems.push(rc.notes.trim().substring(0, 120));
              if (rc.contentStatus && rc.contentStatus !== 'N/A' && (rc.contentStatus.toLowerCase().includes('awaiting') || rc.contentStatus.toLowerCase().includes('pending') || rc.contentStatus.toLowerCase().includes('approval'))) followupItems.push(rc.contentStatus.trim().substring(0, 120));

              const hasFlagItems = blockedText || reasonFlag || nextPlan || winsEntry.length > 0 || followupItems.length > 0;

              return (
                <div key={i} style={{ display: 'flex', gap: 16, marginBottom: 24, position: 'relative' }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%', background: ecfg.bg, border: `3px solid ${ecfg.color}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0, zIndex: 1
                  }}>{ecfg.icon}</div>
                  <div style={{ flex: 1, background: 'white', borderRadius: 10, padding: 16, border: `1px solid ${ecfg.border}`, borderLeft: `4px solid ${ecfg.color}` }}>
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: '#111' }}>{week}</span>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#f3f4f6', color: '#6b7280' }}>
                          {smeta.icon} {smeta.short}
                        </span>
                      </div>
                      <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 10, background: ecfg.bg, color: ecfg.color, fontWeight: 600 }}>
                        {norm(entry.status) === 'At Risk' ? 'Needs Attention' : norm(entry.status)}
                      </span>
                    </div>

                    {/* Content */}
                    {content && (
                      <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, margin: '0 0 10px 0', whiteSpace: 'pre-wrap' }}>
                        {content.length > 300 ? content.slice(0, 300) + '…' : content}
                      </p>
                    )}

                    {/* Metrics */}
                    {metrics.length > 0 && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: hasFlagItems ? 10 : 0 }}>
                        {metrics.map((m, j) => (
                          <span key={j} style={{ padding: '3px 8px', borderRadius: 4, background: '#f3f4f6', fontSize: 11, color: '#4b5563' }}>
                            {m.l}: <strong>{m.v}</strong>
                          </span>
                        ))}
                      </div>
                    )}

                    {/* ── Separate columns: Blocked | Reason | Next | Wins | Follow-Up ── */}
                    {hasFlagItems && (
                      <div style={{ display: 'grid', gridTemplateColumns: [blockedText, reasonFlag, nextPlan, winsEntry.length > 0, followupItems.length > 0].filter(Boolean).length >= 4 ? '1fr 1fr 1fr 1fr' : [blockedText, reasonFlag, nextPlan, winsEntry.length > 0, followupItems.length > 0].filter(Boolean).length === 3 ? '1fr 1fr 1fr' : [blockedText, reasonFlag, nextPlan, winsEntry.length > 0, followupItems.length > 0].filter(Boolean).length === 2 ? '1fr 1fr' : '1fr', gap: 8 }}>
                        {blockedText && (
                          <div style={{ background: '#fef2f2', borderRadius: 6, padding: 10 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#dc2626', letterSpacing: 0.5, marginBottom: 4 }}>🚫 WHAT'S BLOCKED</div>
                            <div style={{ fontSize: 12, color: '#7f1d1d', lineHeight: 1.4 }}>{blockedText}</div>
                          </div>
                        )}
                        {reasonFlag && (
                          <div style={{ background: '#fff7ed', borderRadius: 6, padding: 10 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#c2410c', letterSpacing: 0.5, marginBottom: 4 }}>💡 WHY IT'S DELAYED</div>
                            <div style={{ fontSize: 12, color: '#9a3412', lineHeight: 1.4 }}>
                              <strong>{reasonFlag}</strong>
                              {reasonExplanation && <span> — {reasonExplanation.length > 120 ? reasonExplanation.slice(0, 120) + '…' : reasonExplanation}</span>}
                            </div>
                          </div>
                        )}
                        {nextPlan && (
                          <div style={{ background: '#eff6ff', borderRadius: 6, padding: 10 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#1e40af', letterSpacing: 0.5, marginBottom: 4 }}>📋 WHAT'S PENDING</div>
                            <div style={{ fontSize: 12, color: '#1e3a5f', lineHeight: 1.4 }}>
                              {nextPlan.length > 200 ? nextPlan.slice(0, 200) + '…' : nextPlan}
                            </div>
                          </div>
                        )}
                        {winsEntry.length > 0 && (
                          <div style={{ background: '#f0fdf4', borderRadius: 6, padding: 10 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#166534', letterSpacing: 0.5, marginBottom: 4 }}>✅ WORKING WELL</div>
                            <div style={{ fontSize: 12, color: '#15803d', lineHeight: 1.4 }}>
                              {winsEntry.map((w, wi) => <div key={wi} style={{ marginBottom: wi < winsEntry.length - 1 ? 4 : 0 }}>{w}</div>)}
                            </div>
                          </div>
                        )}
                        {followupItems.length > 0 && (
                          <div style={{ background: '#faf5ff', borderRadius: 6, padding: 10 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#7e22ce', letterSpacing: 0.5, marginBottom: 4 }}>🔔 NEEDS FOLLOW-UP</div>
                            <div style={{ fontSize: 12, color: '#581c87', lineHeight: 1.4 }}>
                              {followupItems.map((f, fi) => <div key={fi} style={{ marginBottom: fi < followupItems.length - 1 ? 4 : 0 }}>{f}</div>)}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

function Loading() {
  return <div style={{ minHeight: '100vh', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ textAlign: 'center', color: '#9ca3af' }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: '#111', marginBottom: 8 }}>IncX<span style={{ color: '#6366f1' }}>Ops</span></div>
      <div>Loading...</div>
    </div>
  </div>;
}
function ErrorState({ message }) {
  return <div style={{ minHeight: '100vh', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ textAlign: 'center', color: '#6b7280' }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: '#111', marginBottom: 4 }}>Something went wrong</div>
      <div style={{ fontSize: 14 }}>{message}</div>
    </div>
  </div>;
}
function EmptyState() {
  return <div style={{ minHeight: '100vh', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No data</div>;
}
function NotFound({ name }) {
  return <div style={{ minHeight: '100vh', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ textAlign: 'center', color: '#6b7280' }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: '#111', marginBottom: 4 }}>Project not found</div>
      <Link to="/" style={{ color: '#6366f1', textDecoration: 'none' }}>← Back to all projects</Link>
    </div>
  </div>;
}

export default ProjectHistory;